import { EAuthenticationType, EDatasourceType, IDatasourceDto } from "@kuika/kuika-cl-model";
import {
  IODataConnectionDto,
  IRestApiEndpoint,
  IRestApiSchema,
  IRestServiceConnectionDto
} from "@kuika/kuika-cl-model/src/dtos/datasource.dto";
import type { KuikaTabOption } from "../../ui-kit/kuika-tabs/kuika-tabs";

/**
 * What separates one API connection form from another. Everything else — the sheet, the
 * authentication block, the header table, the rename, save, the unsaved-changes hash — is
 * the same code in `rest-connection-form.tsx`.
 *
 * The three forms this replaces differed, after normalising the type's name, in their help
 * links, one URL field, and which DTO property they wrote. What they shared was ~340 lines
 * of header and auth machinery that diffed to zero.
 */

/**
 * What both connection DTOs hold. They are identical but for the name of the one URL that
 * describes the service: OData calls it `metadataUrl`, Swagger and REST call it `swaggerUrl`.
 */
export interface IRestConnectionRequest {
  prefix: string;
  serviceBaseUrl: string;
  metadataUrl?: string;
  swaggerUrl?: string;
  clientIdKeyName: string;
  clientId: string;
  clientSecretKeyName: string;
  clientSecret: string;
  currentUserEmailName: string;
  authType: EAuthenticationType;
  extraHeaderParameters: Record<string, string>;
  /** The collection's endpoints and their schemas; carried through untouched on save. */
  paths?: IRestApiEndpoint[];
  schemas?: IRestApiSchema[];
  description?: string;
}

export interface IRestConnectionField {
  /** Form path. The E2E page objects address these inputs as `dsModal_<name>`. */
  name: string;
  label: string;
  placeholder: string;
  /** Shown in the label's tooltip, beside a link to the type's own guide. */
  description: string;
  requiredMessage: string;
  read: (request: IRestConnectionRequest) => string;
  write: (request: IRestConnectionRequest, value: string) => void;
}

export interface IRestConnectionSpec {
  /** Prefix for every derived test id, e.g. `odata`. */
  testIdPrefix: string;
  /** Names the type in copy: "Any note about your <copyName>", and in warnings. */
  copyName: string;
  datasourceType: EDatasourceType;
  /** The type's guide, reached from every field's tooltip. */
  helpLink: string;
  /** What the unsaved-changes modal calls this thing. */
  closeType: string;
  /** Short line under the Endpoint legend. */
  endpointHint: string;
  /** Short line under the Authentication legend. */
  authenticationHint: string;
  descriptionPlaceholder: string;
  fields: IRestConnectionField[];
  getRequest: (datasource: IDatasourceDto) => IRestConnectionRequest | undefined;
  setRequest: (datasource: IDatasourceDto, request: IRestConnectionRequest) => void;
  /**
   * The REST collection hands off to the endpoint editor after saving, because a collection
   * with no endpoints does nothing. The other two just close.
   */
  opensEndpointEditorAfterSave?: boolean;
  /** What CREATE leads to, for the rail's "After you create" — says the hand-off above. */
  afterCreate: string;
}

const CLOSES_AFTER_CREATE: string = "CREATE saves the connection and closes this page.";

const SERVICE_BASE_URL_FIELD = (placeholder: string, description: string): IRestConnectionField => ({
  name: "ServiceBaseURL",
  label: "Service base URL",
  placeholder,
  description,
  requiredMessage: "Please enter a Service Base URL!",
  read: (request: IRestConnectionRequest): string => request.serviceBaseUrl ?? "",
  write: (request: IRestConnectionRequest, value: string): void => {
    request.serviceBaseUrl = value;
  }
});

const SWAGGER_GUIDE: string = "https://academy.kuika.com/en/content/swagger-api-data-source-addition-user-guide";
const REST_GUIDE: string = "https://academy.kuika.com/en/content/rest-api-data-source-user-guide";
const ODATA_GUIDE: string = "https://academy.kuika.com/en/content/user-guide-for-adding-a-data-source-with-odata";

/**
 * Whether a saved connection hands off to the endpoint editor: only a NEW REST collection, which
 * has no endpoints yet and does nothing without one. Saving an existing collection just closes,
 * and a pending navigation ("Save & close", or a tool tab clicked mid-save) is answered instead
 * of mounting an editor only to leave it.
 */
export function shouldOpenEndpointEditorAfterSave(
  spec: IRestConnectionSpec,
  isExistingDatasource: boolean,
  isNavigationPending: boolean
): boolean {
  return spec.opensEndpointEditorAfterSave === true && !isExistingDatasource && !isNavigationPending;
}

export const SWAGGER_SPEC: IRestConnectionSpec = {
  testIdPrefix: "swagger",
  copyName: "Swagger API",
  datasourceType: EDatasourceType.RestApi,
  helpLink: SWAGGER_GUIDE,
  closeType: "Swagger API",
  endpointHint: "Where Kuika connects",
  authenticationHint: "How Kuika identifies itself",
  descriptionPlaceholder: "Any note about your Swagger API",
  afterCreate: CLOSES_AFTER_CREATE,
  fields: [
    {
      name: "SwaggerURL",
      label: "Swagger URL",
      placeholder: "https://petstore.swagger.io/v2/swagger.json",
      description: "The Swagger URL that defines your API and provides its documentation.",
      requiredMessage: "Please enter a Swagger URL!",
      read: (request: IRestConnectionRequest): string => request.swaggerUrl ?? "",
      write: (request: IRestConnectionRequest, value: string): void => {
        request.swaggerUrl = value;
      }
    },
    SERVICE_BASE_URL_FIELD(
      "https://petstore.swagger.io/v2",
      "The URL address that an Swagger service uses as the basis for all its endpoints."
    )
  ],
  getRequest: (datasource: IDatasourceDto): IRestConnectionRequest | undefined => datasource.restApiConnectionRequest,
  setRequest: (datasource: IDatasourceDto, request: IRestConnectionRequest): void => {
    const stored: IRestServiceConnectionDto = { ...request, swaggerUrl: request.swaggerUrl ?? "" };
    datasource.restApiConnectionRequest = stored;
  }
};

export const REST_WO_SWAGGER_SPEC: IRestConnectionSpec = {
  testIdPrefix: "restapi",
  copyName: "REST API",
  datasourceType: EDatasourceType.RestApiWOSwagger,
  helpLink: REST_GUIDE,
  closeType: "Rest API",
  endpointHint: "Where Kuika connects",
  authenticationHint: "How Kuika identifies itself",
  descriptionPlaceholder: "Any note about your REST API Collection",
  afterCreate: "CREATE saves the collection and opens the endpoint editor on a new endpoint.",
  fields: [
    SERVICE_BASE_URL_FIELD(
      "https://petstore.swagger.io/v2",
      "The URL address that a REST API uses as the basis for all its endpoints."
    )
  ],
  getRequest: (datasource: IDatasourceDto): IRestConnectionRequest | undefined => datasource.restApiConnectionRequest,
  setRequest: (datasource: IDatasourceDto, request: IRestConnectionRequest): void => {
    const stored: IRestServiceConnectionDto = { ...request, swaggerUrl: request.swaggerUrl ?? "" };
    datasource.restApiConnectionRequest = stored;
  },
  opensEndpointEditorAfterSave: true
};

export const ODATA_SPEC: IRestConnectionSpec = {
  testIdPrefix: "odata",
  copyName: "OData",
  datasourceType: EDatasourceType.OData,
  helpLink: ODATA_GUIDE,
  closeType: "OData",
  endpointHint: "Where Kuika connects",
  authenticationHint: "How Kuika identifies itself",
  descriptionPlaceholder: "Any note about your OData API",
  afterCreate: CLOSES_AFTER_CREATE,
  fields: [
    {
      name: "metadataUrl",
      label: "Metadata URL",
      placeholder: "https://services.odata.org/TripPinRESTierService/$metadata",
      description: "The metadata document that describes the OData service's entities.",
      requiredMessage: "Please enter a Metadata URL!",
      read: (request: IRestConnectionRequest): string => request.metadataUrl ?? "",
      write: (request: IRestConnectionRequest, value: string): void => {
        request.metadataUrl = value;
      }
    },
    SERVICE_BASE_URL_FIELD(
      "https://services.odata.org/TripPinRESTierService",
      "The URL address that an OData service uses as the basis for all its endpoints."
    )
  ],
  getRequest: (datasource: IDatasourceDto): IRestConnectionRequest | undefined => datasource.oDataConnectionRequest,
  setRequest: (datasource: IDatasourceDto, request: IRestConnectionRequest): void => {
    const stored: IODataConnectionDto = { ...request, metadataUrl: request.metadataUrl ?? "" };
    datasource.oDataConnectionRequest = stored;
  }
};

/** The auth types these forms offer, in the order the segmented control shows them. */
export const REST_AUTH_TYPES: EAuthenticationType[] = [
  EAuthenticationType.NoAuth,
  EAuthenticationType.KuikaAuth,
  EAuthenticationType.Basic,
  EAuthenticationType.ClientIdSecret
];

/** An endpoint may also defer to whatever its collection is configured with — first, as its default. */
export const ENDPOINT_AUTH_TYPES: EAuthenticationType[] = [
  EAuthenticationType.InheritFromCollection,
  ...REST_AUTH_TYPES
];

export const REST_AUTH_TYPE_LABELS: Record<number, string> = {
  [EAuthenticationType.NoAuth]: "No Auth",
  [EAuthenticationType.KuikaAuth]: "Bearer",
  [EAuthenticationType.Basic]: "Basic",
  [EAuthenticationType.ClientIdSecret]: "API Key",
  [EAuthenticationType.InheritFromCollection]: "Inherit from collection"
};

/**
 * Per-option test-id suffix, appended to `<prefix>AuthType` and `Option` by
 * `buildAuthTypeTabOptions`. Kept separate from `REST_AUTH_TYPE_LABELS` so the E2E suite's
 * hook does not move if the visible copy is ever reworded.
 */
export const REST_AUTH_TYPE_TEST_ID_SUFFIXES: Record<number, string> = {
  [EAuthenticationType.NoAuth]: "NoAuth",
  [EAuthenticationType.KuikaAuth]: "Bearer",
  [EAuthenticationType.Basic]: "Basic",
  [EAuthenticationType.ClientIdSecret]: "ApiKey",
  [EAuthenticationType.InheritFromCollection]: "InheritFromCollection"
};

/**
 * The Authentication type's segments. `KuikaTabs` speaks strings, so each value is the enum
 * spelled as one — the form turns it back with `Number(value)`. Every option carries its own
 * `<prefix>AuthType<Suffix>Option` hook: a segmented control has no single element to click
 * through, so the suite addresses the options themselves.
 */
export const buildAuthTypeTabOptions = (authTypes: EAuthenticationType[], testIdPrefix: string): KuikaTabOption[] =>
  authTypes.map((authType: EAuthenticationType): KuikaTabOption => ({
    value: String(authType),
    label: REST_AUTH_TYPE_LABELS[authType],
    dataTestId: `${testIdPrefix}AuthType${REST_AUTH_TYPE_TEST_ID_SUFFIXES[authType]}Option`
  }));
